// ============================================================
// Motor de sincronización con Supabase (background, no bloquea UI)
// ============================================================

import { supabase } from '@/lib/supabase'
import { recordReportReliabilityEvent } from '@/features/reports/reportReliability'
import {
  obtenerPendientes,
  eliminarPendiente,
  incrementarReintentos,
  marcarPendienteFallida,
  eliminarReporteLocalDefinitivo,
  type OpPendiente,
} from '@/lib/localStore'

const MAX_REINTENTOS = 5
const INTERVALO_MS = 10_000
const MISSING_OWNER_ERROR = 'missing usuarioId for scoped sync operation'

let timerActivo: ReturnType<typeof setInterval> | null = null
let procesando = false
let onlineHandler: (() => void) | null = null
let focusHandler: (() => void) | null = null

async function obtenerUsuarioActivoId(): Promise<string | null> {
  try {
    const { data, error } = await supabase.auth.getUser()
    if (error) return null
    return data.user?.id ?? null
  } catch {
    return null
  }
}

export async function procesarOp(op: OpPendiente): Promise<boolean> {
  try {
    if (op.tipo === 'delete' && op.pk) {
      const { error } = await supabase.from(op.tabla).delete().eq('id', op.pk)
      if (error) throw error
      return true
    }
    if (op.tipo === 'insert') {
      const { error } = await supabase.from(op.tabla).insert(op.datos)
      if (error) throw error
      return true
    }
    if (op.tipo === 'upsert') {
      const query = supabase.from(op.tabla)
      const { error } = op.tabla === 'critical_finding_events'
        ? await query.upsert(op.datos, { onConflict: 'report_id,finding_id' })
        : await query.upsert(op.datos)
      if (error) throw error
      return true
    }
    if (op.tipo === 'update' && op.pk) {
      const { error } = await supabase.from(op.tabla).update(op.datos).eq('id', op.pk)
      if (error) throw error
      return true
    }
    return false
  } catch {
    return false
  }
}

export async function procesarCola(): Promise<void> {
  if (procesando) return
  procesando = true
  try {
    const pendientes = await obtenerPendientes()
    const usuarioActivoId = pendientes.some(op => op.usuarioId)
      ? await obtenerUsuarioActivoId()
      : null

    for (const op of pendientes) {
      if (!op.usuarioId) {
        await marcarPendienteFallida(op.id, MISSING_OWNER_ERROR)
        continue
      }

      if (op.usuarioId && op.usuarioId !== usuarioActivoId) continue

      if (op.reintentos >= MAX_REINTENTOS) {
        await marcarPendienteFallida(op.id)
        if (op.tabla === 'reports') {
          await recordReportReliabilityEvent({ userId: op.usuarioId, reportId: op.pk, kind: 'sync_blocked' })
        }
        continue
      }
      const ok = await procesarOp(op)
      if (ok) {
        await eliminarPendiente(op.id)
        // Si fue delete de reporte, limpiar local también
        if (op.tipo === 'delete' && op.tabla === 'reports' && op.pk) {
          await eliminarReporteLocalDefinitivo(op.pk)
        }
      } else {
        await incrementarReintentos(op.id)
        if (op.tabla === 'reports') {
          await recordReportReliabilityEvent({ userId: op.usuarioId, reportId: op.pk, kind: 'sync_retry' })
        }
      }
    }
  } finally {
    procesando = false
  }
}

export function iniciarSyncEngine(): void {
  if (timerActivo) return
  // Procesar una vez al arrancar, luego cada 10s
  void procesarCola()
  timerActivo = setInterval(() => void procesarCola(), INTERVALO_MS)

  if (typeof window === 'undefined') return

  // Procesar cuando el navegador vuelva a estar online
  onlineHandler = () => void procesarCola()
  window.addEventListener('online', onlineHandler)

  // Procesar al enfocar la pestaña
  focusHandler = () => void procesarCola()
  window.addEventListener('focus', focusHandler)
}

export function detenerSyncEngine(): void {
  if (timerActivo) {
    clearInterval(timerActivo)
    timerActivo = null
  }

  if (typeof window !== 'undefined') {
    if (onlineHandler) window.removeEventListener('online', onlineHandler)
    if (focusHandler) window.removeEventListener('focus', focusHandler)
  }
  onlineHandler = null
  focusHandler = null
}

// Forzar sincronización manual
export async function sincronizarAhora(): Promise<void> {
  await procesarCola()
}
